import React, { useEffect, useState } from "react";
import { Table, Input, Tag, Space, Popconfirm, DatePicker, TimePicker, Select } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as SaveIcon } from "../../assets/save-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete-icon.svg";
import { ReactComponent as AttachIcon } from "../../assets/attach.svg";
import { ReactComponent as CloseIcon } from "../../assets/cross-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteDeliveryNote,
  fetchAllDeliveryNotes,
  setDeliveryNoteIsEdit,
  updateDeliveryNote,
} from "./redux/actions";
import SpinnerComponent from "../../components/spinner";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import CustomPagination from "../../components/customPagination/CustomPagination";
import AttachmentContainer from "../Projects/tableSection/expandedRow/AttachmentContainter";
import moment from "moment";
import DeliveryNoteCalender from "./deliveryNoteCalender";

const { RangePicker } = DatePicker;

const DeliveryNotesTable = ({ canUpdate }) => {
  // Format date to "day Month year Hour:Min" format (e.g., "30 Sep 25 05:55")
  const formatDispatchedDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('DD MMM YY HH:mm');
  };

  // Format date to "day month year" format (e.g., "29 Sep 25")
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('DD MMM YY');
  };
  const [sortedInfo, setSortedInfo] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [currentDelRecord, setCurrentDelRecord] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { project_id } = useParams();
  const [page, setPage] = useState(() => 1);
  const [resultPerPage, setResultPerPage] = useState(() => 10);
  const [req, setReq] = useState(false);
  const [search, setSearch] = useState({});
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [dateRange, setDateRange] = useState([null, null]);
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const [deliveryNotePermissions, setDeliveryNotePermissions] = useState({});

  const { allDeliveryNotes, fetchDeliveryNotesLoading, deleteDeliveryNoteLoading, totalRecords } =
    useSelector((state) => state.deliveryNoteReducer);
  const { user_projects } = useSelector((state) => state.userManagerReducer);

  useEffect(() => {
    if (page && resultPerPage && page > 0 && resultPerPage > 0 && project_id) {
      dispatch(
        fetchAllDeliveryNotes({
          project_id,
          page: page,
          perPage: resultPerPage,
          search,
          sort: sortedInfo?.order && sortedInfo?.columnKey,
          order: sortedInfo?.order
            ? sortedInfo?.order === "ascend"
              ? "asc"
              : "desc"
            : null,
        })
      );
    }
  }, [req, project_id]);

  useEffect(() => {
    let paramsPage = 1;
    if (paramsPage && resultPerPage && paramsPage > 0 && resultPerPage > 0 && project_id) {
      dispatch(
        fetchAllDeliveryNotes({ 
          project_id,
          page: paramsPage, 
          perPage: resultPerPage, 
          search 
        })
      );
      setPage(1);
    }
  }, [defaultPageOneReq, project_id]);

  useEffect(() => {
    debouncedCallApi();
  }, [sortedInfo]);

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 1000)
  );

  const [debouncedDefaultPageOneCallApi] = useState(() =>
    _.debounce(() => setDefaultPageOneReq((prev) => !prev), 1000)
  );

  useEffect(() => {
    setDataSource(allDeliveryNotes);
  }, [allDeliveryNotes]);
  
  useEffect(() => {
    setFetchLoading(fetchDeliveryNotesLoading);
  }, [fetchDeliveryNotesLoading]);

  // Extract delivery note permissions for current project
  useEffect(() => {
    const projectPermissions = user_projects?.find((el) => el?.projectId === parseInt(project_id));
    setDeliveryNotePermissions(projectPermissions?.permissions || {});
  }, [user_projects, project_id]);

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  const handleDelete = (record) => {
    setCurrentDelRecord(record);
    dispatch(deleteDeliveryNote(record));
  };

  const handleDateInput = (dates) => {
    setDateRange(dates);
    const dateStrings = dates ? [dates[0]?.format('YYYY-MM-DD'), dates[1]?.format('YYYY-MM-DD')] : ['', ''];
    setSearch({
      ...search,
      startDate: dateStrings[0] || '',
      endDate: dateStrings[1] || '',
    });
    debouncedDefaultPageOneCallApi();
  };

  const searchInput = (key, type = 'text') => {
    if (type === 'date') {
      return (
        <DeliveryNoteCalender
          date={dateRange}
          format="DD MMM YY"
          handleDateInput={handleDateInput}
          setDate={setDateRange}
          setDatePickerOpened={setDatePickerOpened}
        />
      );
    }

    if (type === 'select' && key === 'status') {
      return (
        <Select
          onClick={(e) => e.stopPropagation()}
          onChange={(value) => {
            setSearch({
              ...search,
              [key]: value,
            });
            debouncedDefaultPageOneCallApi();
          }}
          style={{ width: '100%' }}
          size="small"
          allowClear
          options={[
            { value: 'incomplete', label: 'Incomplete' },
            { value: 'completed', label: 'Complete' }
          ]}
        />
      );
    }
    
    return (
      <Input
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          setSearch({
            ...search,
            [key]: e.target.value,
          });
          debouncedDefaultPageOneCallApi();
        }}
      />
    );
  };

  const handleNavigate = (id) =>
    navigate(`/project/delivery-notes/${project_id}/update/${id}`);

  const handleEdit = (record) => {
    setEditingRecord(record.id);
    setEditedData({
      driverName: record.driverName || record.driver,
      driverDate: record.driverArrivedAt ? moment(record.driverArrivedAt).format('DD MMM YY') : '',
      driverTime: record.driverArrivedTime || '',
      receiverName: record.receiverName || record.receiver,
      receivedDate: record.receivedAt ? moment(record.receivedAt).format('DD MMM YY') : '',
      receivedTime: record.receivedAtTime || '',
    });
  };

  const handleSave = (record) => {
    // Prepare the payload for API
    const payload = {};
    
    // Handle driver data - store date and time separately
    if (editedData.driverName !== undefined) {
      payload.driverName = editedData.driverName;
    }
    
    // Handle driver date and time separately
    const driverDate = editedData.driverDate || (record.driverArrivedAt ? moment(record.driverArrivedAt).format('YYYY-MM-DD') : '');
    const driverTime = editedData.driverTime || record.driverArrivedTime || '';
    
    if (driverDate) {
      payload.driverArrivedAt = moment(driverDate, 'DD MMM YY').format('YYYY-MM-DD');
    }
    if (driverTime) {
      payload.driverArrivedTime = driverTime; // Store time in HH:mm format
    }
    
    // Handle receiver data - store date and time separately
    if (editedData.receiverName !== undefined) {
      payload.receiverName = editedData.receiverName;
    }
    
    // Handle receiver date and time separately
    const receivedDate = editedData.receivedDate || (record.receivedAt ? moment(record.receivedAt).format('YYYY-MM-DD') : '');
    const receivedTime = editedData.receivedTime || record.receivedAtTime || '';
    
    if (receivedDate) {
      payload.receivedAt = moment(receivedDate, 'DD MMM YY').format('YYYY-MM-DD');
    }
    if (receivedTime) {
      payload.receivedAtTime = receivedTime; // Store time in HH:mm format
    }
    
    // Auto-complete status when receiverName, receivedAt, and receivedTime are all present
    const finalReceiverName = payload.receiverName || record.receiverName;
    const finalReceivedDate = payload.receivedAt || record.receivedAt;
    const finalReceivedTime = payload.receivedAtTime || record.receivedTime;
    
    if (finalReceiverName && finalReceivedDate && finalReceivedTime) {
      payload.status = "completed";
    }
    
    console.log('Saving data to API:', payload);
    
    // Add the record ID to the payload for the API call
    const apiPayload = {
      ...payload,
      id: record.id
    };
    
    // Dispatch the update action
    dispatch(updateDeliveryNote(apiPayload));
    
    // Update local dataSource with the formatted data for immediate UI feedback
    const updatedDataSource = dataSource.map(item => 
      item.id === record.id 
        ? { 
            ...item, 
            ...editedData,
            ...(payload.driverArrivedAt && { driverArrivedAt: payload.driverArrivedAt }),
            ...(payload.driverArrivedTime && { driverArrivedTime: payload.driverArrivedTime }),
            ...(payload.receiverName && { receiver: payload.receiverName, receiverName: payload.receiverName }),
            ...(payload.receivedAt && { receivedAt: payload.receivedAt }),
            ...(payload.receivedAtTime && { receivedAtTime: payload.receivedAtTime }),
            ...(payload.status && { status: payload.status })
          }
        : item
    );
    setDataSource(updatedDataSource);
    setEditingRecord(null);
    setEditedData({});
  };

  const handleCancel = () => {
    setEditingRecord(null);
    setEditedData({});
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateTimeChange = (recordId, type, dateOrTime, value) => {
    setEditedData(prev => {
      const currentData = prev[`${type}ArrivedAt`] || prev[`${type}At`] || {};
      
      if (dateOrTime === 'date') {
        return {
          ...prev,
          [`${type}Date`]: value,
          [`${type}Time`]: currentData.time || prev[`${type}Time`] || ''
        };
      } else {
        return {
          ...prev,
          [`${type}Date`]: currentData.date || prev[`${type}Date`] || '',
          [`${type}Time`]: value
        };
      }
    });
  };

  const columns = [
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">DATE</div>
            <div>{searchInput("date", "date")}</div>
          </div>
        );
      },
      dataIndex: "date",
      key: "date",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "date" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (date, record) => (
        <div onClick={() => handleNavigate(record.id)}>{formatDate(date)}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">DEL NOTE NO</div>
            <div>{searchInput("delNoteNo")}</div>
          </div>
        );
      },
      dataIndex: "delNoteNo",
      key: "delNoteNo",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "delNoteNo" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (delNoteNo, record) => (
        <div onClick={() => handleNavigate(record.id)}>{delNoteNo}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">TO</div>
            <div>{searchInput("to")}</div>
          </div>
        );
      },
      dataIndex: "to",
      key: "to",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "to" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (to, record) => (
        <div onClick={() => handleNavigate(record.id)}>{to}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">ATTENTION</div>
            <div>{searchInput("attention")}</div>
          </div>
        );
      },
      dataIndex: "attention",
      key: "attention",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "attention" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (attention, record) => (
        <div onClick={() => handleNavigate(record.id)}>{attention}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">RE</div>
            <div>{searchInput("re")}</div>
          </div>
        );
      },
      dataIndex: "re",
      key: "re",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "re" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (re, record) => (
        <div onClick={() => handleNavigate(record.id)}>{re}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">TRUCK REG</div>
            <div>{searchInput("truckTrailerReg")}</div>
          </div>
        );
      },
      dataIndex: "truckTrailerReg",
      key: "truckTrailerReg",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "truckTrailerReg" && sortedInfo?.order,
      // ellipsis: true,
      width: "10%",
      render: (truckTrailerReg, record) => (
        <div onClick={() => handleNavigate(record.id)}>{truckTrailerReg}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">DISPATCHER</div>
            <div>{searchInput("dispatcherName")}</div>
          </div>
        );
      },
      dataIndex: "dispatcherName",
      key: "dispatcherName",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "dispatcherName" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (dispatcherName, record) => (
        <div onClick={() => handleNavigate(record.id)}>
          <div>{dispatcherName}</div>
          <div style={{ fontSize: '10px', color: '#666' }}>
            {formatDispatchedDate(record.dispatchedAt || record.dispatcherDate)}
          </div>
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">DRIVER</div>
            <div>{searchInput("driverName")}</div>
          </div>
        );
      },
      dataIndex: "driverName",
      key: "driverName",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "driverName" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (driverName, record) => {
        const isEditing = editingRecord === record.id;
        
        if (isEditing) {
          return (
            <div onClick={(e) => e.stopPropagation()} style={{ 
              padding: '4px', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '4px' 
            }}>
              <Input
                placeholder="Driver Name"
                value={editedData.driverName || ''}
                onChange={(e) => handleFieldChange('driverName', e.target.value)}
                style={{ fontSize: '12px' }}
                size="small"
              />
              <DatePicker
                className="delivery-note-picker"
                placeholder="Date"
                value={editedData.driverDate ? moment(editedData.driverDate, 'DD MMM YY') : (record.driverArrivedAt ? moment(record.driverArrivedAt) : null)}
                onChange={(date, dateString) => handleDateTimeChange(record.id, 'driver', 'date', dateString)}
                style={{ width: '100%', fontSize: '12px' }}
                size="small"
                format="DD MMM YY"
                suffixIcon={null}
              />
              <TimePicker
                className="delivery-note-picker"
                placeholder="Time Arrived"
                value={editedData.driverTime ? moment(editedData.driverTime, 'HH:mm') : (record.driverArrivedTime ? moment(record.driverArrivedTime, 'HH:mm') : null)}
                onChange={(time, timeString) => handleDateTimeChange(record.id, 'driver', 'time', timeString)}
                style={{ width: '100%', fontSize: '12px' }}
                size="small"
                format="HH:mm"
                suffixIcon={null}
              />
            </div>
          );
        }
        
        return (
          <div onClick={() => handleNavigate(record.id)}>
            <div>{record.driverName || driverName}</div>
            {(record.driverArrivedAt || record.driverArrivedTime) && (
              <div style={{ fontSize: '10px', color: '#666' }}>
                {record.driverArrivedAt ? formatDate(record.driverArrivedAt) : ''}
                {record.driverArrivedAt && record.driverArrivedTime ? ' ' : ''}
                {record.driverArrivedTime || ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">RECEIVER</div>
            <div>{searchInput("receiverName")}</div>
          </div>
        );
      },
      dataIndex: "receiverName",
      key: "receiverName",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "receiverName" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (receiverName, record) => {
        const isEditing = editingRecord === record.id;
        
        if (isEditing) {
          return (
            <div onClick={(e) => e.stopPropagation()} style={{ 
              padding: '4px', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '4px' 
            }}>
              <Input
                placeholder="Receiver Name"
                value={editedData.receiverName || ''}
                onChange={(e) => handleFieldChange('receiverName', e.target.value)}
                style={{ fontSize: '12px' }}
                size="small"
              />
              <DatePicker
                className="delivery-note-picker"
                placeholder="Date"
                value={editedData.receivedDate ? moment(editedData.receivedDate, 'DD MMM YY') : (record.receivedAt ? moment(record.receivedAt) : null)}
                onChange={(date, dateString) => handleDateTimeChange(record.id, 'received', 'date', dateString)}
                style={{ width: '100%', fontSize: '12px' }}
                size="small"
                format="DD MMM YY"
                suffixIcon={null}
              />
              <TimePicker
                className="delivery-note-picker"
                placeholder="Time Received"
                value={editedData.receivedTime ? moment(editedData.receivedTime, 'HH:mm') : (record.receivedAtTime ? moment(record.receivedAtTime, 'HH:mm') : null)}
                onChange={(time, timeString) => handleDateTimeChange(record.id, 'received', 'time', timeString)}
                style={{ width: '100%', fontSize: '12px' }}
                size="small"
                format="HH:mm"
                suffixIcon={null}
              />
            </div>
          );
        }
        
        return (
          <div onClick={() => handleNavigate(record.id)}>
            <div>{record.receiverName || receiverName}</div>
            {(record.receivedAt || record.receivedAtTime) && (
              <div style={{ fontSize: '10px', color: '#666' }}>
                {record.receivedAt ? formatDate(record.receivedAt) : ''}
                {record.receivedAt && record.receivedAtTime ? ' ' : ''}
                {record.receivedAtTime || ''}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">ATTACH</div>
            <div>{searchInput("attachments")}</div>
          </div>
        );
      },
      dataIndex: "attachments",
      key: "attachments",
      width: "8%",
      align: "center",
      render: (_, record) => {
        const isCompleted = record.status?.toLowerCase() === 'completed';
        const canEditIncomplete = deliveryNotePermissions?.delivery_notes?.edit;
        const canEditCompleted = deliveryNotePermissions?.delivery_notes?.edit_completed_delivery_notes;
        const canEditAttachments = isCompleted ? canEditCompleted : canEditIncomplete;

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <AttachmentContainer
              recordType="delivery_note"
              record={record}
              color="#ff6b35"
              visibility={!canEditAttachments}
            />
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">STATUS</div>
            <div>{searchInput("status", "select")}</div>
          </div>
        );
      },
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "status" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      align: "center",
      render: (status, record) => {
        let backgroundColor = "";
        let color = "";
        let text = "";
        
        // Convert to lowercase for case-insensitive comparison
        const statusLower = status?.toLowerCase();
        
        switch (statusLower) {
          case "completed":
            backgroundColor = "#90EE90";
            color = "#030202";
            text = "Completed";
            break;
          case "incomplete":
            backgroundColor = "#FFB6C1";
            color = "#030202";
            text = "Incomplete";
            break;
          default:
            backgroundColor = "#f0f0f0";
            color = "#030202";
            text = status;
            break;
        }
        
        return (
          <div
            onClick={() => handleNavigate(record.id)}
            style={{
              backgroundColor,
              color,
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500',
              textAlign: 'center',
              border: 'none',
              display: 'inline-block',
              minWidth: '80px'
            }}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: null,
      dataIndex: "",
      key: "action",
      align: "center",
      width: "5%",
      render: (_, record) => {
        if (!canUpdate) {
          return;
        }

        const isEditing = editingRecord === record.id;
        
        // Check permissions based on record status
        const isCompleted = record.status?.toLowerCase() === 'completed';
        const canEditIncomplete = deliveryNotePermissions?.delivery_notes?.edit;
        const canEditCompleted = deliveryNotePermissions?.delivery_notes?.edit_completed_delivery_notes;
        
        // Determine if edit icon should be shown
        const shouldShowEditIcon = isCompleted ? canEditCompleted : canEditIncomplete;
        
        if (!shouldShowEditIcon) {
          return null;
        }

        return deleteDeliveryNoteLoading && record.id === currentDelRecord.id ? (
          <SpinnerComponent fontSize={14} />
        ) : (
          <Space size={15} align="baseline">
            {isEditing ? (
              <>
                <SaveIcon
                  width={18}
                  height={18}
                  className="custom-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSave(record);
                  }}
                  style={{ color: '#52c41a' }}
                />
                <CloseIcon
                  width={18}
                  height={18}
                  className="custom-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                  style={{ color: '#ff4d4f' }}
                />
              </>
            ) : (
              <EditIcon
                width={18}
                height={18}
                className="custom-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(record);
                }}
              />
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div className="delivery-notes-table-container">
        <Table
          className="special-table delivery-notes-table"
          columns={columns}
          scroll={{ x: true }}
          onChange={handleChange}
          dataSource={dataSource}
          pagination={false}
          sortUpIcon={<UpOutlined />}
          sortDownIcon={<DownOutlined />}
          loading={fetchLoading}
          rowKey="id"
        />
      </div>
      <CustomPagination
        page={page}
        setPage={setPage}
        resultPerPage={resultPerPage}
        setResultPerPage={setResultPerPage}
        debouncedCallApi={debouncedCallApi}
        totalRecord={totalRecords || dataSource.length}
      />
    </>
  );
};

export default DeliveryNotesTable;
